import { useRouter } from 'next/router';

import { Dispatch, SetStateAction, useCallback, useState } from 'react';

import { UseComboboxSelectedItemChange } from 'downshift';
import { objectToCamel } from 'ts-case-convert';

import { GeographyTypeEnum } from '@nocowanie/core/enums';
import { appendSearchCriteriaParamsToPath } from '@nocowanie/common-ui/helpers';
import { useDebouncedCallback, useSearchCookie } from '@nocowanie/common-ui/hooks';

import { environment } from '@app/environments/environment';
import { AutocompleteItemModel, AutocompleteItemSCModel } from '@app/models/elastic-search';
import { ApiAutocompleteService } from '@app/services/api-autocomplete.service';

export interface UseAutocompleteDataProps {
    itemCount?: number;
    setBaseUrl?: Dispatch<SetStateAction<string | null>>;
    onAccommodationSelected?: () => void;
    onLocationSelected?: (item: AutocompleteItemModel) => void;
    initialQuery?: string;
}

export const useAutocompleteData = ({
    setBaseUrl,
    itemCount = 5,
    initialQuery,
    onAccommodationSelected,
    onLocationSelected,
}: UseAutocompleteDataProps = {}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [innerQuery, setInnerQuery] = useState(initialQuery || '');
    const [items, setItems] = useState<AutocompleteItemModel[] | null>(null);
    const router = useRouter();
    const { getSearchCriteriaFromCookie } = useSearchCookie(environment.COOKIE_DOMAIN);

    const onQueryChange = useDebouncedCallback(async (query: string) => {
        setBaseUrl?.(null);

        if (!query.trim().length) {
            setItems(null);
            return;
        }

        setIsLoading(true);

        try {
            const result = await ApiAutocompleteService.instance.getAutocompleteItems(
                query,
                itemCount,
            );

            const items =
                result?.hits?.map(({ _source }: { _source: AutocompleteItemSCModel }) =>
                    objectToCamel(_source),
                ) || [];

            setItems(items);
        } catch (error) {
            throw new Error(`[useAutocomplete] Request failed: ${error}`);
        } finally {
            setIsLoading(false);
        }
    }, 300);

    const onSelectedItemChange = useCallback(
        (item: UseComboboxSelectedItemChange<AutocompleteItemModel>, withRedirect = true) => {
            const { path, type, details } = item.selectedItem;
            const isAccommodation = type === GeographyTypeEnum.accommodation;
            const targetPath =
                isAccommodation && details?.ota
                    ? appendSearchCriteriaParamsToPath(path, getSearchCriteriaFromCookie())
                    : path;

            setBaseUrl?.(isAccommodation ? (withRedirect ? null : targetPath) : targetPath);

            if (isAccommodation && withRedirect) {
                onAccommodationSelected?.();
                requestAnimationFrame(() => {
                    router.push(targetPath);
                });
            } else if (!isAccommodation) {
                onLocationSelected?.(item.selectedItem);
            }
        },
        [onAccommodationSelected, onLocationSelected, router, setBaseUrl],
    );

    return {
        initialQuery,
        query: innerQuery,
        setQuery: setInnerQuery,
        items,
        isLoading,
        onQueryChange,
        onSelectedItemChange,
    };
};
